import { IssueOutDto } from 'shared/dtos/out/issue';
import { LabelledEntity } from 'shared/types/commonView';
import { CreateIssueModel, IssueModel } from '../../issueModel';

function identity<T>(x: T): T {
  return x;
}

function labelledEntityToString(
  labelledEntity: LabelledEntity | undefined | null
): string | undefined | null {
  if (labelledEntity === null) return labelledEntity;
  return labelledEntity?._id;
}

function makeCreateIssueModelExtendedDataToOutDtoMap(): {
  [key in keyof IssueModel['extendedData']]: (p: any) => any;
} {
  return {
    workTypes: identity,
    rootCauses: identity,
    estimatedCost: costs,
    finalCost: costs,
    targetCompletionDate: identity,
    finalCompletionDate: identity,
    targetResponseToAuthorDate: identity,
    finalResponseToAuthorDate: identity,
    solutionProposal: identity,
    impact: identity,
    environmentalAspect: identity,
    effect: identity,
    hazardCategory: identity,
    expectedFine: identity,
    imposedFine: identity,
    solutionMethod: identity,
    subcontractorRepresentative: identity,
    decisionToImposeFine: identity,
    circumstances: identity,
    issueVerification: identity,
    delayReason: identity,
    rejectionReason: identity,
    proposedCorrectiveAction: identity,
    costCode: identity,
    daysOfInabilityToWork: identity,
    personUnableToWork: identity,
    spilledSubstance: identity,
    waterLeakageScale: identity,
    soilLeakageScale: identity,
    contaminatedSoilScale: identity,
    targetStartDate: identity,
    finalStartDate: identity,
    executedByCompanies: identity,
    targetAmount: identity,
    completedAmount: identity,
    numberOfEmployees: identity,
    priority: identity,
    projectStage: identity,
    sickLeaveStartDate: identity,
    sickLeaveEndDate: identity,
    supervisor: identity,
    victim: identity,
    victimJobTitle: identity,
    employeeNumber: identity,
    employmentType: identity,
    immediateMeasures: identity,
    victimActivity: identity,
    injuryTypes: identity,
    injuredBodyParts: identity,
    applicationNumber: identity,
    productType: identity,
    producerDistributor: identity,
    typeModel: identity,
    installationLocation: identity,
    requirements: identity,
    installationBasis: identity,
    constructionProductDocuments: identity,
    deviceDocuments: identity,
    additionalDocuments: identity,
  };
}

function makeCreateIssueModelPrimaryDataToOutDtoMap(): {
  [key in keyof IssueModel['primaryData']]: (p: any) => any;
} {
  return {
    site: identity,
    level: identity,
    title: identity,
    assignee: labelledEntityToString,
    description: identity,
    detectionDate: identity,
    subcontractors: identity,
    contractNumbers: identity,
    positionOnMap: identity,
    executor: labelledEntityToString,
    selectedLocationType: identity,
    finalAreas: identity,
    targetAreas: identity,
  };
}

function costs(costs: any[]): any {
  return costs.map((cost) => {
    return {
      ...cost,
      coveredBy: cost.coveredBy?._id,
    };
  });
}

export function createIssueModelToOutDto(
  createModel: CreateIssueModel
): Partial<IssueOutDto> {
  // Note: stageId is not sent during creation - backend assigns the first stage
  // based on the workflow configured for the process.
  const outDto: Partial<IssueOutDto> = {
    process: createModel.process,
    protocolItem: createModel.protocolItem,
    inspection: createModel.inspection,
  };

  if (createModel.extendedData) {
    const mappings = makeCreateIssueModelExtendedDataToOutDtoMap();
    outDto.extendedData = {} as IssueOutDto['extendedData'];
    const keys = Object.keys(createModel.extendedData);

    // @ts-ignore Object.keys defined badly
    keys.forEach((key: keyof CreateIssueModel['extendedData']) => {
      if (mappings[key] && createModel.extendedData?.[key] !== undefined) {
        // @ts-ignore Object.keys defined badly
        outDto.extendedData[key] = mappings[key](
          // @ts-ignore Object.keys defined badly
          createModel.extendedData[key]
        );
      }
    });
  }

  if (createModel.primaryData) {
    const mappings = makeCreateIssueModelPrimaryDataToOutDtoMap();
    outDto.primaryData = {} as IssueOutDto['primaryData'];
    const keys = Object.keys(createModel.primaryData);

    // @ts-ignore Object.keys defined badly
    keys.forEach((key: keyof CreateIssueModel['primaryData']) => {
      if (mappings[key] && createModel.primaryData?.[key] !== undefined) {
        // @ts-ignore Object.keys defined badly
        outDto.primaryData[key] = mappings[key](
          // @ts-ignore Object.keys defined badly
          createModel.primaryData[key]
        );
      }
    });
  }

  return outDto;
}
